<script lang="ts" setup>

import { computed, onBeforeUnmount, onMounted, watch } from "vue";

import { AddLayerObject, GeoJSONSource } from "maplibre-gl";

import { addLayer, removeLayersAndSources, injectMap } from '../../../utils/MapUtils';
import { locationColor } from '../../../utils/Utils';
import { locationStore } from "@/RunnerLocation";
import { TLineString } from '@/shared/types/geojson';

const $map = injectMap();

const location = computed(() => {
	return locationStore;
});

const source = computed((): GeoJSONSource => {
	return $map.getSource("location-track") as GeoJSONSource;
});


const historyGeoJson = computed((): TLineString => {

	const coords = location.value.history.map((p: GeolocationPosition) => { // eslint-disable-line
		return [p.coords.longitude, p.coords.latitude];
	});

	const g: TLineString = {
		type: "Feature",
		geometry: {
			type: "LineString",
			coordinates: coords,
		},
		properties: {}
	}
	return g;
});


function initMap() {
	const layer: AddLayerObject = {
		id: "location-track",
		type: 'line',
		source: {
			type: 'geojson',
			lineMetrics: true,
			data: historyGeoJson.value
		},
		layout: {
			'line-cap': 'round',
			'line-join': 'round'
		},
		paint: {
			'line-color': '#ffffff',
			'line-width': 2,
			'line-opacity': 1,
		}
	};
	addLayer($map, layer);

	drawMap();
}

function drawMap() {
	if (!$map.isSourceLoaded("location-track")) {
		return requestAnimationFrame(drawMap)
	}

	if (location.value.history.length <= 1) {
		return;
	}

	const tailTime = location.value.currentTime - location.value.trackLength;
	let lengthToRemove = 0;

	const fraction = (tailTime - location.value.history[0].timestamp) / (location.value.history[1].timestamp - location.value.history[0].timestamp);
	lengthToRemove = location.value.history[1].distance * fraction;

	const totalLength = location.value.history.reduce((total, current, i) => {
		if (i == 0)
			return total;
		return total + current.distance;
	}, 0);

	if (totalLength == 0) {
		return;
	}

	const visibleLength = totalLength - lengthToRemove;
	const fadeAtFraction = 1 - (visibleLength / totalLength);

	const colorBefore = locationColor();
	const colorTrans = "transparent";
	const little = 0.000000001;
	let values: any[] = [];

	values.push(0, colorTrans);
	values.push(Math.max(little, fadeAtFraction), colorTrans);
	// values.push(Math.max(little, fadeAtFraction) + little, colorBefore);
	values.push(1 + little, colorBefore);
	$map.setPaintProperty("location-track", "line-gradient", [
		'interpolate',
		['linear'],
		['line-progress'],
		//@ts-ignore
		...values
	]);
}

watch(() => location.value.currentTime, () => {
	drawMap();
});

watch(() => location.value.history, () => {
	source.value.setData(historyGeoJson.value)
	//drawMap(); //probably not needed since currentTime is changed at the same time (and causes double updates)
}, { deep: true }); // Add deep: true if history is an array/object and you need to watch nested changes


onMounted(() => {
	initMap();
});

onBeforeUnmount(() => {
	removeLayersAndSources($map, ["location-track"]);
});
</script>

<template></template>

<style></style>